/**
 * Google Connection Card Component
 *
 * Displays Google OAuth connection status, quota usage, and connect/disconnect actions.
 * Shows quota as progress bar when connected, or connect button when not connected.
 *
 * IMPORTANT: OAuth must open in new window via App Bridge due to iframe restrictions.
 * Google blocks OAuth consent pages in iframes (clickjacking protection).
 * Uses Redirect.Action.REMOTE to break out of the Shopify admin iframe.
 *
 * @see app/src/workers/google-oauth.ts for OAuth backend implementation
 * @see docs/PRD.md for dashboard requirements
 */

import { useState, useCallback } from 'react';
import {
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Box,
  ProgressBar,
  Modal,
  Banner,
  SkeletonBodyText,
  SkeletonDisplayText,
} from '@shopify/polaris';
import type { GoogleConnectionStatus, ApiResult } from '../types/api';
import { disconnectGoogle, getShopDomain } from '../lib/api-client';

// =============================================================================
// Types
// =============================================================================

export interface GoogleConnectionCardProps {
  /** Google connection status data */
  status: GoogleConnectionStatus | null;
  /** Whether data is loading */
  loading?: boolean;
  /** Error message if fetch failed */
  error?: string | null;
  /** Callback when status changes (connect/disconnect) */
  onStatusChange?: () => void;
}

// =============================================================================
// Constants
// =============================================================================

/** OAuth endpoint path for initiating Google OAuth flow */
const GOOGLE_OAUTH_PATH = '/auth/google';

/** Worker URL for API and OAuth endpoints */
const WORKER_URL = 'https://pingdex-worker.dan-5f0.workers.dev';

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get the base URL for the app (used for OAuth redirect)
 * Returns the Worker URL since API and OAuth endpoints are on the Worker
 */
function getAppBaseUrl(): string {
  return WORKER_URL;
}

/**
 * Open URL in a new window (for OAuth flow)
 * Uses App Bridge Redirect.Action.REMOTE to break out of iframe
 *
 * @param url - The URL to open
 */
function openExternalUrl(url: string): void {
  // App Bridge v4+ doesn't have the same Redirect API as v3
  // For v4, we use direct window.open which works because the button click
  // triggers a user gesture that allows popup opening
  //
  // Note: The OAuth endpoint will redirect the user back to the Shopify admin
  // after completing the OAuth flow via buildShopifyAdminUrl in google-oauth.ts
  if (typeof window !== 'undefined') {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/**
 * Calculate progress bar percentage
 */
function calculateProgress(used: number, limit: number): number {
  if (limit === 0) return 100;
  return Math.min(Math.round((used / limit) * 100), 100);
}

/**
 * Get progress bar tone based on usage percentage
 * Note: ProgressBar supports 'highlight' | 'primary' | 'success' | 'critical'
 * We use 'highlight' for the "approaching limit" state (80-99%)
 */
function getProgressTone(percentage: number, exhausted: boolean): 'success' | 'highlight' | 'critical' {
  if (exhausted || percentage >= 100) return 'critical';
  if (percentage >= 80) return 'highlight';
  return 'success';
}

// =============================================================================
// Skeleton Component
// =============================================================================

/**
 * Loading skeleton for GoogleConnectionCard
 */
function GoogleConnectionCardSkeleton() {
  return (
    <Card>
      <BlockStack gap="400">
        <SkeletonBodyText lines={1} />
        <SkeletonDisplayText size="small" />
        <SkeletonBodyText lines={2} />
      </BlockStack>
    </Card>
  );
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * Card displaying Google OAuth connection status, quota usage, and actions.
 *
 * Features:
 * - Shows 'Not Connected' state with 'Connect Google' button
 * - Shows 'Connected' state with quota usage progress bar
 * - Displays 'Quota Exhausted' badge when quota is 0 remaining
 * - Disconnect button with confirmation modal
 * - Connect button uses new window for OAuth flow (iframe restrictions)
 * - Loading skeleton state
 * - Error banner for fetch failures
 *
 * @example
 * ```tsx
 * <GoogleConnectionCard
 *   status={googleStatus}
 *   loading={isLoading}
 *   error={errorMessage}
 *   onStatusChange={handleRefresh}
 * />
 * ```
 */
export function GoogleConnectionCard({
  status,
  loading = false,
  error = null,
  onStatusChange,
}: GoogleConnectionCardProps) {
  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);

  // Handle connect button click
  const handleConnect = useCallback(() => {
    const shopDomain = getShopDomain();
    if (!shopDomain) {
      console.error('[GoogleConnectionCard] Could not determine shop domain');
      return;
    }

    setConnecting(true);

    // Build OAuth URL with shop parameter
    const baseUrl = getAppBaseUrl();
    const oauthUrl = `${baseUrl}${GOOGLE_OAUTH_PATH}?shop=${encodeURIComponent(shopDomain)}`;

    // Open in new window to avoid iframe restrictions
    openExternalUrl(oauthUrl);

    // Reset connecting state after a brief delay
    // The actual connection status will be refreshed when user returns
    setTimeout(() => {
      setConnecting(false);
      // Trigger status refresh after OAuth flow completes
      // User may have connected, so refresh the status
      onStatusChange?.();
    }, 2000);
  }, [onStatusChange]);

  // Handle disconnect confirmation
  const handleDisconnect = useCallback(async () => {
    setDisconnecting(true);
    setDisconnectError(null);

    try {
      const result: ApiResult<void> = await disconnectGoogle();

      if (result.success) {
        setDisconnectModalOpen(false);
        // Trigger status refresh
        onStatusChange?.();
      } else {
        setDisconnectError(result.error.error);
      }
    } catch (err) {
      setDisconnectError('Failed to disconnect. Please try again.');
    } finally {
      setDisconnecting(false);
    }
  }, [onStatusChange]);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    if (!disconnecting) {
      setDisconnectModalOpen(false);
      setDisconnectError(null);
    }
  }, [disconnecting]);

  // Loading state
  if (loading) {
    return <GoogleConnectionCardSkeleton />;
  }

  // Error state
  if (error) {
    return (
      <Card>
        <BlockStack gap="300">
          <Text as="h3" variant="headingSm" tone="subdued">
            Google Connection
          </Text>
          <Banner tone="critical">
            {error}
          </Banner>
        </BlockStack>
      </Card>
    );
  }

  // Not connected state
  if (!status || !status.connected) {
    return (
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h3" variant="headingSm" tone="subdued">
              Google Connection
            </Text>
            <Badge>Not Connected</Badge>
          </InlineStack>

          <Text as="p" variant="bodyMd" tone="subdued">
            Connect your Google account to ping Google's Indexing API for faster indexing.
            IndexNow already handles Bing, Yandex, and others.
          </Text>

          <Box>
            <Button
              variant="primary"
              onClick={handleConnect}
              loading={connecting}
            >
              Connect Google
            </Button>
          </Box>

          <Banner tone="info">
            Google has a daily quota limit (default 200/day).
            You can request an increase from Google if needed.
          </Banner>
        </BlockStack>
      </Card>
    );
  }

  // Connected state - show quota usage
  const { quotaUsed, quotaLimit, quotaExhausted } = status;
  const quotaRemaining = Math.max(0, quotaLimit - quotaUsed);
  const progressPercentage = calculateProgress(quotaUsed, quotaLimit);
  const progressTone = getProgressTone(progressPercentage, quotaExhausted);

  return (
    <>
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h3" variant="headingSm" tone="subdued">
              Google Connection
            </Text>
            <InlineStack gap="200">
              {quotaExhausted && (
                <Badge tone="critical">Quota Exhausted</Badge>
              )}
              <Badge tone="success">Connected</Badge>
            </InlineStack>
          </InlineStack>

          {/* Quota usage display */}
          <BlockStack gap="200">
            <InlineStack align="space-between" blockAlign="center">
              <Text as="p" variant="bodyMd">
                Today's Quota
              </Text>
              <Text as="p" variant="bodyMd" fontWeight="semibold">
                {quotaUsed} / {quotaLimit}
              </Text>
            </InlineStack>

            <ProgressBar
              progress={progressPercentage}
              tone={progressTone}
              size="small"
            />

            <Text as="p" variant="bodySm" tone="subdued">
              {quotaRemaining > 0
                ? `${quotaRemaining} pings remaining today`
                : 'No quota remaining. Resets at midnight Pacific Time.'}
            </Text>
          </BlockStack>

          {/* Quota exhausted warning */}
          {quotaExhausted && (
            <Banner tone="warning">
              Google quota is exhausted for today. IndexNow pings are still active.
              Quota resets at midnight Pacific Time.
            </Banner>
          )}

          {/* Disconnect button */}
          <Box>
            <Button
              variant="plain"
              tone="critical"
              onClick={() => setDisconnectModalOpen(true)}
            >
              Disconnect Google
            </Button>
          </Box>
        </BlockStack>
      </Card>

      {/* Disconnect confirmation modal */}
      <Modal
        open={disconnectModalOpen}
        onClose={handleModalClose}
        title="Disconnect Google?"
        primaryAction={{
          content: 'Disconnect',
          destructive: true,
          loading: disconnecting,
          onAction: handleDisconnect,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            disabled: disconnecting,
            onAction: handleModalClose,
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p" variant="bodyMd">
              This will remove your Google account connection. Pingdex will no longer
              ping Google's Indexing API when your content changes.
            </Text>
            <Text as="p" variant="bodyMd">
              IndexNow pings (Bing, Yandex, etc.) will continue working normally.
            </Text>
            {disconnectError && (
              <Banner tone="critical">
                {disconnectError}
              </Banner>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </>
  );
}
